/*!
 * animMath.js — the animation cues' pure arithmetic
 * © 2026 Rob Canning — GPLv3
 *
 * No DOM, no window: these load in node, where test/animMath.test.js runs
 * them. trans.js, fade.js and o2p.js import them.
 */

// trans(): map a whole-list progress t (0-1, the quantity the sync system
// carries as cfg._currentT) back onto a leg. Forward legs: the leg into
// waypoint i spans [i/N, (i+1)/N]. Walking back in mode:alternate, the leg
// into waypoint j spans (j+2)/N down to (j+1)/N, and the final leg home
// spans 1/N down to 0.
export function legForT(t, dir, N) {
  t = Math.max(0, Math.min(1, Number(t) || 0));
  if (!(N > 0)) return { index: 0, frac: 0, direction: 1 };
  if (dir >= 0) {
    const index = Math.min(N - 1, Math.floor(t * N));
    return { index, frac: t * N - index, direction: 1 };
  }
  if (t < 1 / N) return { index: -1, frac: 1 - t * N, direction: -1 };   // heading home
  const j = Math.max(0, Math.min(N - 2, Math.floor(t * N) - 1));
  return { index: j, frac: ((j + 2) / N - t) * N, direction: -1 };
}

// fade(): whole-cycle progress (0-1) of a running anime instance, for
// network sync: the phase within the current pass for looping/alternating
// modes (anime's `progress` spans the whole run, Infinity long for loop:true).
export function fadePhaseOf(inst) {
  const p = Number(inst?.iterationProgress);
  return Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : 0;
}

// fade(mode:blink) runs on a timer, not anime: its phase is the elapsed time
// within one period.
export function blinkPhase(now, startedAt, periodMs) {
  if (!(periodMs > 0)) return 0;
  const t = ((now - startedAt) % periodMs) / periodMs;
  return t < 0 ? t + 1 : t;
}

/** o2p: the raw phase an ease maps to p (eases are monotonic; linear is exact) */
export function invertEase(fn, p) {
  if (fn(0.5) === 0.5 && fn(0.25) === 0.25) return p;
  let lo = 0, hi = 1;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (fn(mid) < p) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

const NUM = /[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g;

// numbers per segment of each curve command; its last pair is where it ends
const CURVE_ARGS = { C: 6, S: 4, Q: 4, T: 2, A: 7 };

/**
 * o2p's vertex events: where each corner of a closed path sits, as a
 * fraction of the path's length (0..1, the first corner at 0), from its d
 * attribute. The corners are the points of M, L, H and V (absolute or
 * relative); a curve between them counts towards the length (by its chord)
 * but has no corner of its own. An open path or fewer than three corners
 * gives null. The fractions follow the side lengths, so an irregular
 * polygon has its corners where the object meets them.
 */
export function polygonVertexTs(d) {
  if (typeof d !== "string" || !/[zZ]\s*$/.test(d.trim())) return null;
  const commands = d.match(/[A-Za-z][^A-Za-z]*/g);
  if (!commands) return null;
  const pts = [];                      // [x, y, isCorner]
  let x = 0, y = 0;
  const to = (nx, ny, corner) => { x = nx; y = ny; pts.push([x, y, corner]); };
  for (const cmd of commands) {
    const c = cmd[0];
    const C = c.toUpperCase();
    const rel = c !== C;
    const n = (cmd.slice(1).match(NUM) || []).map(Number);
    if (C === "Z") continue;
    if (C === "M" || C === "L") {
      for (let i = 0; i + 1 < n.length; i += 2) to(rel ? x + n[i] : n[i], rel ? y + n[i + 1] : n[i + 1], true);
    } else if (C === "H") {
      for (const v of n) to(rel ? x + v : v, y, true);
    } else if (C === "V") {
      for (const v of n) to(x, rel ? y + v : v, true);
    } else if (CURVE_ARGS[C]) {
      const k = CURVE_ARGS[C];
      for (let i = 0; i + k <= n.length; i += k) {
        const ex = n[i + k - 2], ey = n[i + k - 1];
        to(rel ? x + ex : ex, rel ? y + ey : ey, false);
      }
    }
  }
  // a last point drawn back onto the first is the same corner
  if (pts.length > 1 && pts.at(-1)[0] === pts[0][0] && pts.at(-1)[1] === pts[0][1]) pts.pop();
  if (pts.filter(p => p[2]).length < 3 || !pts[0][2]) return null;
  const at = [0];                      // the length up to each point
  for (let i = 1; i < pts.length; i++) at.push(at[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  const total = at.at(-1) + Math.hypot(pts[0][0] - pts.at(-1)[0], pts[0][1] - pts.at(-1)[1]);
  if (!(total > 0)) return null;
  return pts.flatMap((p, i) => (p[2] ? [at[i] / total] : []));
}

/**
 * o2p: has pathT crossed a vertex since lastT? Returns the vertex index
 * (0-based), or -1.
 */
export function checkVertexCrossing(lastT, currentT, vertices) {
  if (!vertices || vertices.length === 0) return -1;
  if (lastT === null || lastT === undefined) return -1;

  const n = vertices.length;
  const from = lastT;
  const to = currentT;

  // Determine actual movement direction, accounting for wraparound
  // Large jumps (> 0.5) indicate wraparound - flip the apparent direction
  const rawDiff = to - from;
  const isWraparound = Math.abs(rawDiff) > 0.5;
  const movingForward = isWraparound ? (rawDiff < 0) : (rawDiff >= 0);

  for (let i = 0; i < n; i++) {
    const vt = vertices[i];

    if (movingForward) {
      // Moving forward: crossed if we went from before to at/after vertex
      if (from < vt && to >= vt) return i;
      // vertex 0 (at t=0): crossing from small t to larger t on the first
      // pass through (not wraparound) catches the initial vertex
      if (vt === 0 && from < 0.02 && to >= 0.02 && from < to) return i;
      // wraparound (from ~1.0 to ~0.0 crosses vertex 0)
      if (from > 0.9 && to < 0.1 && vt < 0.05) return i;
    } else {
      // Moving backward: crossed if we went from after to at/before vertex
      if (from > vt && to <= vt) return i;
      // vertex 0 going backward: crossing down through ~0
      if (vt === 0 && from >= 0.02 && to < 0.02 && from > to) return i;
      // wraparound going backward (from ~0.0 to ~1.0 crosses vertex 0)
      if (from < 0.1 && to > 0.9 && vt < 0.05) return i;
    }
  }

  return -1;
}
